import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import ls from "local-storage";
import { v4 as uuidv4 } from "uuid";
import ColorSteps from "./ColorSteps";
import ColorForm from "./ColorForm";
import ColorSass from "./ColorSass";
import ColorPaletteDetails from "./ColorPaletteDetails";

/**
 * Main component for viewing and editing a color palette.
 * Handles state management for palette details and steps, as well as local storage persistence.
 * @extends Component
 */

class ColorPalette extends Component {
  constructor(props) {
    super(props);
    const palettes = ls.get("palettes") || [];
    const paletteId = props.router.location.pathname.split("/").pop();
    const formType = paletteId ? undefined : "add";
    const paletteDetails = {
      paletteName: "New Palette",
    };
    const palette = palettes.find((element) => element.id === paletteId) || {};
    this.state = {
      colorFormType: formType,
      colorFormStep: {},
      steps: palette.steps || [],
      id: palette.id || uuidv4(),
      paletteDetails: palette.paletteDetails || paletteDetails,
      owner: true,
      readOnly: false,
    };
    this.editStep = {};
    this.colorFormRef = React.createRef();
  }

  /**
   * Merge `palette` into component state and persist locally.
   * @param {Object} palette Partial state to merge
   */
  save(palette) {
    this.setState(palette, this.saveLocal);
  }

  /**
   * Persist the current palette state to local storage.
   * Creates or updates the palette entry in the saved palettes array.
   */
  saveLocal() {
    const palettes = ls.get("palettes") || [];
    const currentState = this.state;
    delete currentState.toast;
    const paletteIndex = palettes.findIndex(
      (element) => element.id === currentState.id,
    );

    if (paletteIndex === -1) {
      palettes.push(currentState);
    } else {
      palettes[paletteIndex] = currentState;
    }

    ls.set("palettes", palettes);
  }

  /**
   * Remove a palette by `id` from local storage and navigate home.
   * @param {string} id Palette id to remove
   */
  deleteLocal(id) {
    if (!id) {
      return;
    }

    let palettes = ls.get("palettes") || [];
    let filteredPalettes = palettes.filter((element) => element.id !== id);
    const currentState = this.state;
    delete currentState.toast;

    ls.set("palettes", filteredPalettes);

    this.setState({ toast: { open: true, title: "Palette Deleted" } }, () => {
      this.props.router.navigate("/");
    });
  }

  /**
   * Update palette details and persist to local storage.
   * @param {Object} details Palette details object
   */
  onSetPaletteDetails(details) {
    this.setState({ paletteDetails: details.paletteDetails }, this.saveLocal);
  }

  /**
   * Add a new color step to the palette and persist.
   * @param {Object} step New step object
   */
  onAddStep(step) {
    const newSteps = [...this.state.steps];
    newSteps.push(step);

    this.setState({ steps: newSteps }, (e) => {
      this.saveLocal();
      this.hideForm();
    });
  }

  /**
   * Replace an existing step with updated data and persist.
   * @param {Object} step Updated step object
   */
  onUpdateStep(step) {
    const updatedSteps = [...this.state.steps];
    const stepIndex = updatedSteps.findIndex(
      (element) => element.id === step.id,
    );

    updatedSteps[stepIndex] = step;

    this.setState({ steps: updatedSteps }, (e) => {
      this.saveLocal();
      this.hideForm();
    });
  }

  /**
   * Remove a step from the palette and persist.
   * @param {Object} step Step object to remove
   */
  onDeleteStep(step) {
    const newSteps = [...this.state.steps].filter(
      (element) => element.id !== step.id,
    );
    this.setState({ steps: newSteps }, this.saveLocal);
  }

  /**
   * Hide the add/edit form and clear the active step.
   */
  hideForm() {
    this.setState({
      colorFormType: undefined,
      colorFormStep: undefined,
    });
  }

  /**
   * Open the form in edit mode for the provided step.
   * @param {Object} step Step to edit
   */
  onEditStep(step) {
    this.setState({
      colorFormType: "edit",
      colorFormStep: step,
    });
  }

  /**
   * Open the form in add mode with an empty step.
   */
  showAddStep() {
    this.setState({
      colorFormType: "add",
      colorFormStep: {},
    });
  }

  render() {
    return (
      <Fragment>
        {Object.keys(this.state.paletteDetails).length > 0 && (
          <ColorPaletteDetails
            palette={this.state}
            onSave={(palette) => this.onSetPaletteDetails(palette)}
            onDelete={(id) => this.deleteLocal(id)}
          />
        )}
        {this.state.colorFormType && (
          <ColorForm
            ref={this.colorFormRef}
            onCancel={() => this.hideForm()}
            onEditStep={(step) => this.onUpdateStep(step)}
            onAddStep={(step) => this.onAddStep(step)}
            step={this.state.colorFormStep}
            type={this.state.colorFormType}
          />
        )}
        {Object.keys(this.state.paletteDetails).length > 0 && (
          <ColorSteps
            owner={this.state.owner}
            steps={this.state.steps}
            paletteDetails={this.state.paletteDetails}
            onAddStep={() => this.showAddStep()}
            onDeleteStep={(step) => this.onDeleteStep(step)}
            onEditStep={(step) => this.onEditStep(step)}
            onSavePalette={() => this.saveLocal()}
          />
        )}
        <hr />
        {Object.keys(this.state.steps).length > 0 && (
          <ColorSass
            steps={this.state.steps}
            paletteDetails={this.state.paletteDetails}
            onAddStep={() => this.showAddStep()}
            onDeleteStep={(step) => this.onDeleteStep(step)}
            onEditStep={(step) => this.onEditStep(step)}
            onSavePalette={() => this.save()}
          />
        )}
      </Fragment>
    );
  }
}

ColorPalette.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.object,
  }),
};

export default ColorPalette;
